import { setOptions, importLibrary } from "@googlemaps/js-api-loader";

// Pinned to 3.64: DrawingManager removed in v3.65. Remove pin once the drawing
// tools (map/google/drawing.js, the shift-rectangle select in
// map/google/interactions.js) migrate off google.maps.drawing.
const VERSION = "3.64";

// What the first map view needs. Everything the app draws at start-up (the
// map, Data layer, ImageMapType overlays, legacy Markers, InfoWindows) comes
// with "maps". Nothing in the app uses google.maps.geometry, so it is no longer
// loaded. Drawing and places load on first use (loadDrawing / loadPlaces).
export const FIRST_VIEW_LIBRARIES = ["maps"];

let optionsSet = false;
// library name -> Promise of that library, shared by every caller.
const libraries = new Map();
let mapsPromise = null;

function loadLibrary(name) {
  if (!optionsSet) {
    // The key is a build-time env var and no language/region is passed (Google
    // picks the browser language, as before), so this can run before React.
    setOptions({ key: process.env.REACT_APP_GOOGLE_MAPS_KEY, v: VERSION });
    optionsSet = true;
  }
  if (!libraries.has(name)) {
    const promise = importLibrary(name).catch((error) => {
      // Forget a failed load (bad key, blocked network...) so the next caller
      // retries instead of getting the same rejection forever.
      libraries.delete(name);
      throw error;
    });
    libraries.set(name, promise);
  }
  return libraries.get(name);
}

/**
 * Load the Google Maps JS API with the libraries the first view needs and
 * resolve with the global `google` namespace. Idempotent: every caller (boot
 * preload in index.js, GoogleMapsEngine.init, the MCA preview...) shares one
 * promise. The library requests are issued in the same tick so the bootstrap
 * script asks for all of them in one request.
 */
const load = function () {
  if (!mapsPromise) {
    mapsPromise = Promise.all(FIRST_VIEW_LIBRARIES.map(loadLibrary))
      .then(() => window.google)
      .catch((error) => {
        mapsPromise = null;
        throw error;
      });
  }
  return mapsPromise;
};

/** google.maps.drawing (DrawingManager, OverlayType), loaded on first use. */
export function loadDrawing() {
  return load().then(() => loadLibrary("drawing"));
}

/** google.maps.places (AutocompleteSuggestion, Place...), loaded on first use. */
export function loadPlaces() {
  return load().then(() => loadLibrary("places"));
}

export default load;
